import { makeRequest, toCorrectFormDataValue, } from '@/js/libs/api';
import * as Types from './types';
import { encodeBody, fromDateType as fromDate, toDateType as toDate, fromDateTimeType as fromDateTime, toDateTimeType as toDateTime, } from '@/js/libs/api-helpers';
export { fromDate, toDate, fromDateTime, toDateTime };
/** Fetches aggregated impression counts based on a specified time range, filters, and grouping dimensions. */ export const getImpressionAggregates = async ({ params, onUploadProgress, signal, baseURL, isInternal, headers: headersParam = {}, queryDefaults, ...otherParams }) => {
    if (params.body === undefined) {
        throw new Error('Missing required body: body');
    }
    const path = '/api/ws-clickstream-analytics/impression-aggregates';
    const pathInternal = '/api/impression-aggregates';
    let body = {};
    const formData = new FormData();
    const urlParams = new URLSearchParams();
    if (params.body !== undefined) {
        body = params.body;
    }
    const headers = {};
    headers['Accept'] = 'application/json';
    headers['Content-Type'] = 'application/json';
    return makeRequest({
        ...otherParams,
        method: 'POST',
        url: isInternal ? pathInternal : path,
        params: urlParams,
        data: encodeBody(body),
        onUploadProgress,
        signal,
        baseURL: baseURL || undefined,
        headers: { ...headers, ...headersParam },
        responseTypes: 'json',
        requestType: 'json',
    });
};
getImpressionAggregates.paramsOrder = [{ type: 'body', paramName: 'body' }];
/** Get report for bidding events */ export const getBiddingReport = async ({ onUploadProgress, signal, baseURL, isInternal, headers: headersParam = {}, queryDefaults, ...otherParams }) => {
    const path = '/api/ws-clickstream-analytics/bidding-report';
    const pathInternal = '/api/bidding-report';
    let body = {};
    const formData = new FormData();
    const urlParams = new URLSearchParams();
    const headers = {};
    headers['Accept'] = 'application/json';
    headers['Content-Type'] = 'application/json';
    return makeRequest({
        ...otherParams,
        method: 'GET',
        url: isInternal ? pathInternal : path,
        params: urlParams,
        data: {},
        onUploadProgress,
        signal,
        baseURL: baseURL || undefined,
        headers: { ...headers, ...headersParam },
        responseTypes: 'json',
        requestType: 'json',
    });
};
getBiddingReport.paramsOrder = [];
/** Get report for BTA Feed Events */ export const getBtaFeedReport = async ({ onUploadProgress, signal, baseURL, isInternal, headers: headersParam = {}, queryDefaults, ...otherParams }) => {
    const path = '/api/ws-clickstream-analytics/bta-feed-report';
    const pathInternal = '/api/bta-feed-report';
    let body = {};
    const formData = new FormData();
    const urlParams = new URLSearchParams();
    const headers = {};
    headers['Accept'] = 'application/json';
    headers['Content-Type'] = 'application/json';
    return makeRequest({
        ...otherParams,
        method: 'GET',
        url: isInternal ? pathInternal : path,
        params: urlParams,
        data: {},
        onUploadProgress,
        signal,
        baseURL: baseURL || undefined,
        headers: { ...headers, ...headersParam },
        responseTypes: 'json',
        requestType: 'json',
    });
};
getBtaFeedReport.paramsOrder = [];
/** Get report for BTA Feed Partners with RTB metrics by bidder */ export const getBtaFeedPartnersReport = async ({ params, onUploadProgress, signal, baseURL, isInternal, headers: headersParam = {}, queryDefaults, ...otherParams }) => {
    const path = '/api/ws-clickstream-analytics/bta-feed-partners-report';
    const pathInternal = '/api/bta-feed-partners-report';
    let body = {};
    const formData = new FormData();
    const urlParams = new URLSearchParams();
    {
        const value = queryDefaults === false ? params.page : (params.page ?? queryDefaults?.['page']);
        if (value !== undefined) {
            urlParams.append('page', value.toString());
        }
    }
    {
        const value = queryDefaults === false ? params.pageSize : (params.pageSize ?? queryDefaults?.['pageSize']);
        if (value !== undefined) {
            urlParams.append('pageSize', value.toString());
        }
    }
    const headers = {};
    headers['Accept'] = 'application/json';
    headers['Content-Type'] = 'application/json';
    return makeRequest({
        ...otherParams,
        method: 'GET',
        url: isInternal ? pathInternal : path,
        params: urlParams,
        data: {},
        onUploadProgress,
        signal,
        baseURL: baseURL || undefined,
        headers: { ...headers, ...headersParam },
        responseTypes: 'json',
        requestType: 'json',
    });
};
getBtaFeedPartnersReport.paramsOrder = [{ type: 'parameters' }];
/**
 * Aggregates adView.duration viewability events. watched_ms is a cumulative per-view timer, so each
 * (ad_id, view_number) view is collapsed with MAX, then summed per day / tag_id / creative_id / page_domain.
 * Returns raw ad-server ids (creative_id) plus additive view_count and sum_watched_ms; the ws-bi report
 * batch job resolves creative_id to ad-console entities and computes the weighted average (sum/count).
 */ export const getAdEngagementReport = async ({ params, onUploadProgress, signal, baseURL, isInternal, headers: headersParam = {}, queryDefaults, ...otherParams }) => {
    if (params.body === undefined) {
        throw new Error('Missing required body: body');
    }
    const path = '/api/ws-clickstream-analytics/ad-engagement-report';
    const pathInternal = '/api/ad-engagement-report';
    let body = {};
    const formData = new FormData();
    const urlParams = new URLSearchParams();
    if (params.body !== undefined) {
        body = params.body;
    }
    const headers = {};
    headers['Accept'] = 'application/json';
    headers['Content-Type'] = 'application/json';
    return makeRequest({
        ...otherParams,
        method: 'POST',
        url: isInternal ? pathInternal : path,
        params: urlParams,
        data: encodeBody(body),
        onUploadProgress,
        signal,
        baseURL: baseURL || undefined,
        headers: { ...headers, ...headersParam },
        responseTypes: 'json',
        requestType: 'json',
    });
};
getAdEngagementReport.paramsOrder = [{ type: 'body', paramName: 'body' }];
/** Fetches aggregated header analytics including header loaded counts, page impressions, visitor counts, and session counts based on a specified time range, filters, and grouping dimensions. */ export const getHeaderAnalytics = async ({ params, onUploadProgress, signal, baseURL, isInternal, headers: headersParam = {}, queryDefaults, ...otherParams }) => {
    if (params.body === undefined) {
        throw new Error('Missing required body: body');
    }
    const path = '/api/ws-clickstream-analytics/header-analytics';
    const pathInternal = '/api/header-analytics';
    let body = {};
    const formData = new FormData();
    const urlParams = new URLSearchParams();
    if (params.body !== undefined) {
        body = params.body;
    }
    const headers = {};
    headers['Accept'] = 'application/json';
    headers['Content-Type'] = 'application/json';
    return makeRequest({
        ...otherParams,
        method: 'POST',
        url: isInternal ? pathInternal : path,
        params: urlParams,
        data: encodeBody(body),
        onUploadProgress,
        signal,
        baseURL: baseURL || undefined,
        headers: { ...headers, ...headersParam },
        responseTypes: 'json',
        requestType: 'json',
    });
};
getHeaderAnalytics.paramsOrder = [{ type: 'body', paramName: 'body' }];
/** Exports aggregated header analytics data as a CSV file, including header loaded counts, page impressions, visitor counts, and session counts based on a specified time range, filters, and grouping dimensions. */ export const exportHeaderAnalyticsCsv = async ({ params, onUploadProgress, signal, baseURL, isInternal, headers: headersParam = {}, queryDefaults, ...otherParams }) => {
    if (params.body === undefined) {
        throw new Error('Missing required body: body');
    }
    const path = '/api/ws-clickstream-analytics/header-analytics/csv';
    const pathInternal = '/api/header-analytics/csv';
    let body = {};
    const formData = new FormData();
    const urlParams = new URLSearchParams();
    if (params.body !== undefined) {
        body = params.body;
    }
    const headers = {};
    headers['Accept'] = 'application/octet-stream';
    headers['Content-Type'] = 'application/json';
    return makeRequest({
        ...otherParams,
        method: 'POST',
        url: isInternal ? pathInternal : path,
        params: urlParams,
        data: encodeBody(body),
        onUploadProgress,
        signal,
        baseURL: baseURL || undefined,
        headers: { ...headers, ...headersParam },
        responseTypes: 'blob',
        requestType: 'json',
    });
};
exportHeaderAnalyticsCsv.paramsOrder = [{ type: 'body', paramName: 'body' }];
/** Fetches aggregated ad impression analytics including ad loaded counts, impression counts, visitor counts, session counts, and average winning bids based on a specified time range, filters, and grouping dimensions. */ export const getAdImpressionAnalytics = async ({ params, onUploadProgress, signal, baseURL, isInternal, headers: headersParam = {}, queryDefaults, ...otherParams }) => {
    if (params.body === undefined) {
        throw new Error('Missing required body: body');
    }
    const path = '/api/ws-clickstream-analytics/ad-impression-analytics';
    const pathInternal = '/api/ad-impression-analytics';
    let body = {};
    const formData = new FormData();
    const urlParams = new URLSearchParams();
    if (params.body !== undefined) {
        body = params.body;
    }
    const headers = {};
    headers['Accept'] = 'application/json';
    headers['Content-Type'] = 'application/json';
    return makeRequest({
        ...otherParams,
        method: 'POST',
        url: isInternal ? pathInternal : path,
        params: urlParams,
        data: encodeBody(body),
        onUploadProgress,
        signal,
        baseURL: baseURL || undefined,
        headers: { ...headers, ...headersParam },
        responseTypes: 'json',
        requestType: 'json',
    });
};
getAdImpressionAnalytics.paramsOrder = [{ type: 'body', paramName: 'body' }];
/** Fetches unique div IDs filtered by optional company IDs and header tag IDs. */ export const getDivIds = async ({ params, onUploadProgress, signal, baseURL, isInternal, headers: headersParam = {}, queryDefaults, ...otherParams }) => {
    const path = '/api/ws-clickstream-analytics/div-ids';
    const pathInternal = '/api/div-ids';
    let body = {};
    const formData = new FormData();
    const urlParams = new URLSearchParams();
    {
        const value = queryDefaults === false ? params.companyIds : (params.companyIds ?? queryDefaults?.['companyIds']);
        if (value !== undefined) {
            urlParams.append('companyIds', value.toString());
        }
    }
    {
        const value = queryDefaults === false ? params.headerTagIds : (params.headerTagIds ?? queryDefaults?.['headerTagIds']);
        if (value !== undefined) {
            urlParams.append('headerTagIds', value.toString());
        }
    }
    {
        const value = queryDefaults === false ? params.query : (params.query ?? queryDefaults?.['query']);
        if (value !== undefined) {
            urlParams.append('query', value.toString());
        }
    }
    {
        const value = queryDefaults === false ? params.limit : (params.limit ?? queryDefaults?.['limit']);
        if (value !== undefined) {
            urlParams.append('limit', value.toString());
        }
    }
    const headers = {};
    headers['Accept'] = 'application/json';
    return makeRequest({
        ...otherParams,
        method: 'GET',
        url: isInternal ? pathInternal : path,
        params: urlParams,
        data: {},
        onUploadProgress,
        signal,
        baseURL: baseURL || undefined,
        headers: { ...headers, ...headersParam },
        responseTypes: 'json',
        requestType: 'json',
    });
};
getDivIds.paramsOrder = [{ type: 'parameters' }];
/** Fetches all unique bidder codes stored in the system. */ export const getBidderCodes = async ({ params, onUploadProgress, signal, baseURL, isInternal, headers: headersParam = {}, queryDefaults, ...otherParams }) => {
    const path = '/api/ws-clickstream-analytics/bidder-codes';
    const pathInternal = '/api/bidder-codes';
    let body = {};
    const formData = new FormData();
    const urlParams = new URLSearchParams();
    {
        const value = queryDefaults === false ? params.query : (params.query ?? queryDefaults?.['query']);
        if (value !== undefined) {
            urlParams.append('query', value.toString());
        }
    }
    {
        const value = queryDefaults === false ? params.limit : (params.limit ?? queryDefaults?.['limit']);
        if (value !== undefined) {
            urlParams.append('limit', value.toString());
        }
    }
    const headers = {};
    headers['Accept'] = 'application/json';
    return makeRequest({
        ...otherParams,
        method: 'GET',
        url: isInternal ? pathInternal : path,
        params: urlParams,
        data: {},
        onUploadProgress,
        signal,
        baseURL: baseURL || undefined,
        headers: { ...headers, ...headersParam },
        responseTypes: 'json',
        requestType: 'json',
    });
};
getBidderCodes.paramsOrder = [{ type: 'parameters' }];
/**
 * Fetches unique system winners. For RTB winners, returns bidder codes.
 * For IO winners, returns company IDs with resolved company names.
 */ export const getSystemWinners = async ({ params, onUploadProgress, signal, baseURL, isInternal, headers: headersParam = {}, queryDefaults, ...otherParams }) => {
    const path = '/api/ws-clickstream-analytics/system-winners';
    const pathInternal = '/api/system-winners';
    let body = {};
    const formData = new FormData();
    const urlParams = new URLSearchParams();
    {
        const value = queryDefaults === false ? params.query : (params.query ?? queryDefaults?.['query']);
        if (value !== undefined) {
            urlParams.append('query', value.toString());
        }
    }
    {
        const value = queryDefaults === false ? params.limit : (params.limit ?? queryDefaults?.['limit']);
        if (value !== undefined) {
            urlParams.append('limit', value.toString());
        }
    }
    {
        const value = queryDefaults === false ? params.winnerType : (params.winnerType ?? queryDefaults?.['winnerType']);
        if (value !== undefined) {
            urlParams.append('winnerType', value.toString());
        }
    }
    const headers = {};
    headers['Accept'] = 'application/json';
    return makeRequest({
        ...otherParams,
        method: 'GET',
        url: isInternal ? pathInternal : path,
        params: urlParams,
        data: {},
        onUploadProgress,
        signal,
        baseURL: baseURL || undefined,
        headers: { ...headers, ...headersParam },
        responseTypes: 'json',
        requestType: 'json',
    });
};
getSystemWinners.paramsOrder = [{ type: 'parameters' }];
/**
 * Fetches system winners matching the supplied codes. For RTB winners the code is
 * the bidder code; for IO winners the code is the company ID (display name resolved
 * to the company name).
 */ export const getSystemWinnersByCodes = async ({ params, onUploadProgress, signal, baseURL, isInternal, headers: headersParam = {}, queryDefaults, ...otherParams }) => {
    if (params.codes === undefined) {
        throw new Error('Missing required parameter: codes');
    }
    const path = '/api/ws-clickstream-analytics/system-winners/by-codes';
    const pathInternal = '/api/system-winners/by-codes';
    let body = {};
    const formData = new FormData();
    const urlParams = new URLSearchParams();
    if (params.codes !== undefined) {
        params.codes.forEach((item) => urlParams.append('codes', item.toString()));
    }
    const headers = {};
    headers['Accept'] = 'application/json';
    return makeRequest({
        ...otherParams,
        method: 'GET',
        url: isInternal ? pathInternal : path,
        params: urlParams,
        data: {},
        onUploadProgress,
        signal,
        baseURL: baseURL || undefined,
        headers: { ...headers, ...headersParam },
        responseTypes: 'json',
        requestType: 'json',
    });
};
getSystemWinnersByCodes.paramsOrder = [{ type: 'parameters' }];
export * from './types';
export * from './constants';
